import {
	DialogBackdropState,
	renderReactDialog,
	Step,
	Toolbar,
	useConfirmationDialog
} from "client/Components";
import { HubItemCategoryKind } from "client/Api/Hub/hub";
import {
	HubItemCategoryNodeKind,
	HubItemElementKind,
	HubItemImageRestrictionsModel,
	HubItemMediaKind
} from "client/Api/Hub/common";
import {
	EmbedElementSaveModel,
	ExistingHubItemImages,
	FrequentlyAskedQuestionElementSaveModel,
	HubHierarchyListCategoryNodeModel,
	HubHierarchyListNodeModel,
	HubImageMediaKind,
	HubItemColumnSaveModel,
	HubItemElementSaveModel,
	HubItemLayoutSaveModel,
	HubLinkedDocumentSaveModel,
	HubItemRowSaveModel,
	ParagraphElementSaveModel,
	SubtitleElementSaveModel,
	VideoElementSaveModel
} from "client/Api/Hub/common.composer";
import { UsersAndUserGroupsDataModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useDataInputDialog,
	useStableImperativeCallback,
	useUnmountedCancellation,
	useUserTimezone,
	useValueEqualMemo,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	EMPTY_ARRAY,
	ensureNotNil,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { deepComparison } from "client/Misc/compareUtils";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import {
	doesNotExceedLength,
	isInt32,
	hasMaximumEntries,
	inline,
	isConvertible,
	isNotEmpty,
	isNotNullNorUndefined,
	isPositive,
	isUrl,
	isValidDate
} from "client/Validation/rules";
import {
	configureValidationStep,
	IObjectValidationStepBuilder
} from "client/Validation/steps";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	HubCategorySavedResult,
	useEditHubCategoryDialog
} from "../../EditHubCategoryDialog/EditHubCategoryDialog";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	EventSaveModel,
	EditEventPageViewModel,
	getNewEventModel,
	getEditEventModel,
	EventViewModel,
	EventStatus,
	createEvent,
	updateEvent,
	EventSaveRequestModel,
	LocationType,
	AttendeeIdWithKind,
	listAttendeesForExport
} from "client/Api/EventsManagement/eventsManagement";
import { ContentStep, ContentStepHandle } from "./ContentStep";
import { SettingsStep } from "./SettingsStep";
import { WhoHasAccessStep } from "./WhoHasAccessStep";
import {
	IObjectValidatorBuilder,
	IValidationConfiguration
} from "client/Validation/builder";
import { IObjectInputAttachable, IValueInputAttachable } from "client/Forms/attachable";
import { HubItemComposerDialog } from "../../HubItemComposerDialog/HubItemComposerDialog";
import {
	cleanUpMedia,
	convertLayoutToSaveModel,
	convertMediaToSaveModel,
	getOriginalImagesFromLayout
} from "client/Features/Hub/converters";
import { ArchivedContentStep } from "./ArchivedContentStep";
import { DetailsStep } from "./DetailsStep";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";
import { AttendeesStep } from "./AttendeesStep";
import { MAX_TAG_COUNT } from "client/Features/Hub/Builder/HubTagSelect";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	createLayoutValidation,
	createMediaValidation
} from "client/Features/Hub/Builder/validation";
import {
	convertToLinkedDocumentFormData,
	convertToLinkedDocumentSaveModel,
	MAX_LINKED_DOCUMENTS_COUNT
} from "client/Features/Hub/Management/LinkedDocuments";
import { useHubComposerUnsavedChangesDialog } from "../../HubComposerUnsavedChangesDialog";
import { ObjectWithClientId } from "client/Misc/clientId";
import { never } from "client_legacy/ndaw/errors";
import {
	getDisableTheShowAutomaticallySettingDialogProps,
	HubItemLayoutFormModel,
	HubItemMediaFormModel,
	tryFindHubCategoryItemNode
} from "../../common";
import { Preview } from "./Preview";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { AttendeeExportModel } from "client/Api/Hub/Events/eventViewer";
import { downloadAttendeesAsCsv } from "client/Features/Hub/Events/Attendees/AttendeesDialog";
import { InfoAlertOptions, useInfoAlerts } from "../../HubItemComposerDialog/useInfoAlerts";
import { DocumentLocationViewModel } from "client/Api/Hub/hubSettings";
import { getLinkedDocumentId } from "client/Features/Hub/useAddDocuments";
import { LinkedDocumentFormData } from "client/Features/Hub/common";

enum FormStep {
	Content = 1,
	Details = 2,
	Settings = 3,
	WhoHasAccess = 4,
	Attendees = 5
}

export type EventFormModel = OverrideProperties<
	EventSaveModel,
	{
		linkedDocuments: readonly LinkedDocumentFormData[];
		media: HubItemMediaFormModel;
		layout: HubItemLayoutFormModel;
	}
>;

type FormModel = OverrideProperties<
	EventSaveRequestModel,
	{
		eventSaveModel: EventFormModel;
	}
>;

const OPTIONAL_STEPS: readonly FormStep[] = [
	FormStep.Attendees
];

export type EditEventDialogMode = {
	kind: "edit";
	eventId: number;
} | {
	kind: "new";
} | {
	kind: "new-from-template";
	templateId: number;
} | {
	kind: "duplicate";
	eventToCopyId: number;
};

export type EditDialogProps = {
	mode: EditEventDialogMode;
	onClose(wasAnyCategoryCreated: boolean): void;
	onSave?(): void;
	onBack?(wasAnyCategoryCreated: boolean): void;
	preferredCategoryId?: number | null;
};

export const EditDialog = (props: EditDialogProps): ReactElement => {
	const {
		mode: modeFromProps,
		onClose: onCloseFromProps,
		onSave,
		onBack,
		preferredCategoryId = null
	} = props;

	const mode = useValueEqualMemo(
		(): EditEventDialogMode => modeFromProps,
		[modeFromProps]
	);

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [activeStep, setActiveStep] = useState<FormStep>(FormStep.Content);
	const [isInPreviewMode, setIsInPreviewMode] = useState<boolean>(false);
	const [finishedSteps, setFinishedSteps] = useState<ReadonlySet<FormStep>>(new Set());
	const [hasServerError, setHasServerError] = useState<boolean>(false);
	const [wasAnyCategoryCreated, setWasAnyCategoryCreated] = useState<boolean>(false);
	const [shouldFocusTitle, setShouldFocusTitle] = useState<boolean>(
		mode.kind === "new"
			? true
			: !isMobile
	);

	const [formData, setFormData] = useState<FormModel>({
		eventSaveModel: {
			id: null,
			title: "",
			layout: { rows: [] },
			media: {
				mediaKind: HubItemMediaKind.None
			},
			isForEveryoneInTheCategory: true,
			access: null,
			removedAttendeeIds: [],
			categoryId: null,
			startTime: null,
			endTime: null,
			publicationDate: null,
			locationType: null,
			location: null,
			onlineEventUrl: null,
			ticketType: null,
			signUpDeadline: null,
			availableSpots: null,
			externalUrl: null,
			tags: [],
			showAutomatically: false,
			linkedDocuments: [],
			isAttendanceEnabled: false,
			shouldShowAuthor: false,
			areCommentsEnabled: false
		},
		shouldPublishEvent: false
	});
	const [initialFormData, setInitialFormData] = useState<FormModel>();
	const [state, setState] = useState<EditEventState>({ isLoaded: false });
	const [hasPublicationDate, setHasPublicationDate] = useState<boolean>(false);
	const [backdropState, setBackdropState] = useState<DialogBackdropState>();

	const {
		categoryHierarchy = EMPTY_ARRAY,
		imageRestrictions = {
			maxImageSize: 0,
			supportedFileExtensions: EMPTY_ARRAY,
			supportedMimeTypes: EMPTY_ARRAY
		},
		defaultImageLocation = null,
		originalImages = EMPTY_IMAGE_SET,
		usersAndUserGroupsData = EMPTY_USER_AND_USERGROUP_DATA,
		status = EventStatus.Draft,
		hasPermissionToCreateNewCategory = false,
		canSendDirectPopups = false,
		attendeeIdsWithKind = [],
		isPixabayImageGalleryAvailable = false
	} = state;

	const infoAlerts: ReactElement = useInfoAlerts(
		useMemo(
			(): readonly InfoAlertOptions[] => [
				{
					uniqueKey: "archived-message",
					message: localize("_ThisEventCantBeEditedBecauseArchived"),
					shouldShow: state.status === EventStatus.Archived,
				},
				{
					uniqueKey: "duplicate-info",
					message: localize("_DuplicateHubItemInfoMessage"),
					shouldShow: mode.kind === "duplicate"
				}
			],
			[mode.kind, state.status]
		)
	);

	const contentStepRef: RefObject<ContentStepHandle | null> = useRef<ContentStepHandle>(null);
	const [confirmationDialogElement, openConfirmationDialog] = useConfirmationDialog();
	const [leaveComposerDialogElement, openLeaveComposerDialog] = useHubComposerUnsavedChangesDialog();
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const onClose: typeof onCloseFromProps = useStableImperativeCallback(onCloseFromProps);
	const userTimezone: TimeZone = useUserTimezone();

	const [
		editCategoryDialog,
		openEditCategoryDialog
	] = useEditHubCategoryDialog();

	const validationConfiguration = useMemo(
		(): IValidationConfiguration<FormModel> => {
			const result: IValidationConfiguration<FormModel> = staticValidationConfiguration.clone();

			const saveModelValidation: IObjectValidatorBuilder<EventFormModel> = result
				.forObject("eventSaveModel");

			saveModelValidation.forObject("media")
				.addValidators(
					createMediaValidation(
						imageRestrictions.supportedMimeTypes,
						imageRestrictions.maxImageSize
					)
				);

			saveModelValidation.forObject("layout")
				.addValidators(
					createLayoutValidation(
						imageRestrictions.supportedMimeTypes,
						imageRestrictions.maxImageSize
					)
				);

			if (hasPublicationDate) {
				saveModelValidation
					.forScalar("publicationDate")
					.add(isNotNullNorUndefined());
			}

			return result;
		},
		[
			hasPublicationDate,
			imageRestrictions
		]
	);

	const formHandler: IFormHandler<FormModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const steps = useMemo(
		(): readonly Step<FormStep>[] => {
			const isAttendanceEnabled: boolean = formHandler
				.value
				.eventSaveModel
				.isAttendanceEnabled;

			const result: readonly Step<FormStep>[] = isAttendanceEnabled
				? [
					...initialSteps,
					{
						title: localize("_Attendees"),
						value: FormStep.Attendees,
						valueTrackingId: "attendees-step"
					}
				] : initialSteps;

			return result.map((x): Step<FormStep> => ({
				...x,
				isFinished: finishedSteps.has(x.value) || mode.kind === "edit",
				hasError: formHandler
					.forStep(formStepToValidationStep[x.value])
					.validationState
					.kind === InputValidationResultKind.Invalid
			}));
		},
		[
			mode.kind,
			finishedSteps,
			formHandler
		]
	);

	const previousStep = useMemo(
		(): FormStep | undefined => {
			const activeIndex: number = steps
				.findIndex(s => s.value === activeStep);

			return steps[activeIndex - 1]
				?.value;
		},
		[
			activeStep,
			steps
		]
	);

	const nextStep = useMemo(
		(): FormStep | undefined => {
			const activeIndex: number = steps
				.findIndex(s => s.value === activeStep);

			return steps[activeIndex + 1]
				?.value;
		},
		[
			activeStep,
			steps
		]
	);

	const isPublishAvailable = useMemo(
		(): boolean => {
			if (status === EventStatus.Archived) {
				return false;
			}

			const isFormValid: boolean = formHandler.validation
				.getValidationState()
				.kind === InputValidationResultKind.Valid;

			return steps
				.filter(x => !OPTIONAL_STEPS.includes(x.value))
				.every(
					x => x.isFinished === true || x.value === activeStep
				)
					&& isFormValid;
		},
		[
			formHandler.validation,
			steps,
			activeStep,
			status
		]
	);

	const isSaveAsDraftAvailable = useMemo(
		(): boolean => {
			if (status !== EventStatus.Draft) {
				return false;
			}

			return Boolean(
				steps.find(
					x => x.value === FormStep.Settings
				)?.isFinished
			);
		},
		[status, steps]
	);

	const isDataChanged = useMemo(
		(): boolean => isNotNil(initialFormData)
			&& !deepComparison(formData, initialFormData),
		[
			formData,
			initialFormData
		]
	);

	const checkIfDataRelevantForUserNotificationChanged = useCallback(
		(): boolean => {
			if (isNil(initialFormData)) {
				return false;
			}

			const oldModel: EventSaveModel = prepareEvent(
				initialFormData.eventSaveModel,
				hasPublicationDate
			);

			const newModel: EventSaveModel = prepareEvent(
				formData.eventSaveModel,
				hasPublicationDate
			);

			return getChangedPropertiesForEventAttendanceNotification(oldModel, newModel)
				.size > 0;
		},
		[
			formData,
			initialFormData,
			hasPublicationDate
		]
	);

	useEffect(
		() => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);
					let model: EditEventPageViewModel;

					switch (mode.kind) {
						case "new":
							model = await getNewEventModel(
								undefined,
								undefined,
								signal
							);
							break;
						case "new-from-template":
							model = await getNewEventModel(
								undefined,
								mode.templateId,
								signal
							);
							break;
						case "duplicate":
							model = await getNewEventModel(
								mode.eventToCopyId,
								undefined,
								signal
							);
							break;
						case "edit":
							model = await getEditEventModel(
								mode.eventId,
								signal
							);
							break;
						default:
							never(mode);
					}

					const {
						eventModel,
						usersAndUserGroupsData,
						eventHierarchy,
						imageRestrictions,
						defaultImageLocation,
						hasPermissionToCreateNewCategory,
						canSendDirectPopups,
						attendeeIdsWithKind,
						createdBy,
						isPixabayImageGalleryAvailable
					} = model;

					let existingPreferredCategoryId: number | null = null;

					if (isNotNil(preferredCategoryId)) {
						const tryFindCategoryResult: TryResult<HubHierarchyListCategoryNodeModel> = tryFindHubCategoryItemNode(eventHierarchy, preferredCategoryId);

						if (tryFindCategoryResult.wasSuccessful) {
							existingPreferredCategoryId = tryFindCategoryResult
								.result
								.category
								.id;
						}
					}

					setState({
						isLoaded: true,
						categoryHierarchy: eventHierarchy,
						usersAndUserGroupsData,
						attendeeIdsWithKind,
						imageRestrictions,
						defaultImageLocation,
						hasPermissionToCreateNewCategory,
						canSendDirectPopups,
						status: mode.kind === "new"
							? EventStatus.Draft
							: eventModel.status,
						originalImages: {
							coverImage: eventModel.media.mediaKind === HubItemMediaKind.Image
								&& eventModel.media.imageKind === HubImageMediaKind.BlobId
									? eventModel.media
									: null,
							layoutImages: isNotNil(eventModel.layout)
								? getOriginalImagesFromLayout(eventModel.layout)
								: new Map()
						},
						originalViewModel: eventModel,
						createdBy,
						isPixabayImageGalleryAvailable
					});

					setHasPublicationDate(isNotNil(eventModel.publicationDate));

					const saveModel: FormModel = await convertToFormModel(
						eventModel,
						existingPreferredCategoryId
					);

					setInitialFormData(saveModel);
					setFormData(saveModel);

					setIsBusy(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose(false);
				}
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			onClose,
			preferredCategoryId,
			mode
		]
	);

	useEffect(
		() => {
			if (
				formHandler.validation
					.getValidationState()
					.kind === InputValidationResultKind.Invalid
						&& hasServerError
			) {
				for (const step of steps) {
					const hasError = formHandler.validation
						.getValidationStepStates(formStepToValidationStep[step.value])
						.some(x => x.kind === InputValidationResultKind.Invalid);

					if (hasError) {
						setActiveStep(step.value);
						setHasServerError(false);
						break;
					}
				}
			}
		},
		[
			formHandler.validation,
			hasServerError,
			steps
		]
	);

	useEffect(
		(): void => {
			if (activeStep !== FormStep.Content) {
				setShouldFocusTitle(false);
			}
		},
		[activeStep]
	);

	const onBeforeUnload = useCallback(
		(event: BeforeUnloadEvent) => {
			const isChanged: boolean = isDataChanged
				|| (
					contentStepRef.current
						?.getIsContentChanged() ?? false
				);

			if (isChanged) {
				beforeUnloadListener(event);
			}
		},
		[isDataChanged]
	);

	useEffect(
		() => {
			window
				.addEventListener(
					"beforeunload",
					onBeforeUnload
				);

			return () => window
				.removeEventListener(
					"beforeunload",
					onBeforeUnload
				);
		},
		[onBeforeUnload]
	);

	const newCategoryHandler = useCallback(
		async (): Promise<void> => {
			setBackdropState("invisible");

			const newCategoryResult: TryResult<HubCategorySavedResult> = await openEditCategoryDialog({
				kind: HubItemKind.Event,
				id: null
			});

			setBackdropState("visible");
			if (!newCategoryResult.wasSuccessful) {
				return;
			}

			const {
				id,
				name
			} = newCategoryResult.result;

			setState(
				state => {
					if (!state.isLoaded) {
						console.error("Cannot add a new category to not loaded state.");
						return state;
					}

					const newCategoryNode: HubHierarchyListCategoryNodeModel = {
						kind: HubItemCategoryNodeKind.Category,
						category: {
							id,
							name,
							canCreateItem: true,
							isFollowing: false,
							hubItemKind: HubItemKind.Event,
							kind: HubItemCategoryKind.Custom
						}
					};

					return {
						...state,
						categoryHierarchy: [
							...state.categoryHierarchy,
							newCategoryNode
						]
					};
				}
			);

			setWasAnyCategoryCreated(true);

			setFormData(
				formData => ({
					...formData,
					eventSaveModel: {
						...formData.eventSaveModel,
						categoryId: id
					}
				})
			);

			formHandler
				.validation
				.for("eventSaveModel")
				.for("categoryId")
				.triggerInputFinished();
		},
		[
			formHandler.validation,
			openEditCategoryDialog
		]
	);

	const tryEnsureValidShowAutomaticallySetting = useCallback(
		async (model: EventFormModel, isDraft: boolean = false): Promise<TryResult<EventFormModel>> => {
			if (!canSendDirectPopups
				&& model.showAutomatically
			) {
				const result: TryResult<void> = await openConfirmationDialog(
					getDisableTheShowAutomaticallySettingDialogProps(isDraft)
				);

				if (!result.wasSuccessful) {
					return { wasSuccessful: false };
				}

				const showAutomaticallyHandler: IValueInputAttachable<boolean> = formHandler
					.for("eventSaveModel")
					.for("showAutomatically");

				showAutomaticallyHandler
					.reducer
					.setValue(false);

				showAutomaticallyHandler
					.validation
					.triggerInputFinished();

				return {
					wasSuccessful: true,
					result: {
						...model,
						showAutomatically: false
					}
				};
			}

			return {
				wasSuccessful: true,
				result: model
			};
		},
		[
			canSendDirectPopups,
			formHandler,
			openConfirmationDialog
		]
	);

	const publishHandler = useCallback(
		async (): Promise<void> => {
			if (status === EventStatus.Archived) {
				return;
			}

			setIsBusy(true);

			try {
				const result: InputValidationResult = await formHandler
					.validation
					.validate();

				if (result.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					setIsInPreviewMode(false);
					return;
				}

				let model: EventFormModel = formData.eventSaveModel;

				const ensureValidShowAutomaticallySettingResult: TryResult<EventFormModel> = await tryEnsureValidShowAutomaticallySetting(model);

				if (!ensureValidShowAutomaticallySettingResult.wasSuccessful) {
					setIsBusy(false);
					return;
				}

				model = ensureValidShowAutomaticallySettingResult.result;

				switch (mode.kind) {
					case "new":
					case "new-from-template":
					case "duplicate":
						await createEvent({
							eventSaveModel: prepareEvent(
								model,
								hasPublicationDate
							),
							shouldPublishEvent: true
						});
						break;
					case "edit":
						let shouldSendNotifications: boolean = false;
						const { isAttendanceEnabled } = model;

						if (status === EventStatus.Published
							&& state.originalViewModel?.isAttendanceEnabled
							&& isAttendanceEnabled
						) {
							const isChanged: boolean = checkIfDataRelevantForUserNotificationChanged();

							if (isChanged) {
								const confirmationDialogResult: TryResult<void> = await openConfirmationDialog({
									trackingId: "send-an-activity-about-this-item",
									title: localize("_DoYouWantToNotifyAllAttendeesAboutTheChanges?"),
									confirmText: localize("_YesSendAnActivity"),
									cancelText: localize("_NoDontSend")
								});

								shouldSendNotifications = confirmationDialogResult
									.wasSuccessful;
							}
						}

						await updateEvent(
							mode.eventId,
							{
								eventSaveModel: prepareEvent(
									model,
									hasPublicationDate
								),
								shouldPublishEvent: true,
								shouldSendNotifications
							}
						);
						break;
					default:
						never(mode);
				}

				setIsBusy(false);
				alertManager.showSuccess(localize("_SuccessfullySubmittedTheEvent"));
				onSave?.();
				onClose(wasAnyCategoryCreated);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				setIsBusy(false);

				const wasHandled: boolean = formHandler
					.validation
					.tryProcessServerError(e);

				if (wasHandled) {
					setIsInPreviewMode(false);
				} else {
					alertManager.showError(localize("_ChangesNotSaved"));
				}

				setHasServerError(true);
			}
		},
		[
			alertManager,
			mode,
			formData.eventSaveModel,
			formHandler.validation,
			checkIfDataRelevantForUserNotificationChanged,
			onClose,
			onSave,
			openConfirmationDialog,
			status,
			wasAnyCategoryCreated,
			hasPublicationDate,
			state.originalViewModel,
			tryEnsureValidShowAutomaticallySetting
		]
	);

	const saveDraftHandler = useCallback(
		async (): Promise<void> => {
			if (status === EventStatus.Archived) {
				return;
			}

			setIsBusy(true);

			try {
				const result: InputValidationResult = await formHandler
					.validation
					.validate();

				if (result.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					setIsInPreviewMode(false);
					return;
				}


				let model: EventFormModel = formData.eventSaveModel;
				const ensureValidShowAutomaticallySettingResult: TryResult<EventFormModel> = await tryEnsureValidShowAutomaticallySetting(model, true);

				if (!ensureValidShowAutomaticallySettingResult.wasSuccessful) {
					setIsBusy(false);
					return;
				}

				model = ensureValidShowAutomaticallySettingResult.result;

				switch (mode.kind) {
					case "new":
					case "new-from-template":
					case "duplicate":
						await createEvent({
							eventSaveModel: prepareEvent(
								model,
								hasPublicationDate
							),
							shouldPublishEvent: false
						});
						break;
					case "edit":
						await updateEvent(
							mode.eventId,
							{
								eventSaveModel: prepareEvent(
									model,
									hasPublicationDate
								),
								shouldPublishEvent: false,
								shouldSendNotifications: null
							}
						);
						break;
					default:
						never(mode);
				}

				setIsBusy(false);
				alertManager.showSuccess(localize("_SuccessfullyCreatedADraft"));
				onSave?.();
				onClose(wasAnyCategoryCreated);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				setIsBusy(false);
				const wasHandled: boolean = formHandler
					.validation
					.tryProcessServerError(e);

				if (wasHandled) {
					setIsInPreviewMode(false);
				}	else {
					alertManager.showError(localize("_ChangesNotSaved"));
				}
				setHasServerError(true);
			}
		},
		[
			alertManager,
			formData.eventSaveModel,
			formHandler.validation,
			mode,
			hasPublicationDate,
			wasAnyCategoryCreated,
			onClose,
			onSave,
			status,
			tryEnsureValidShowAutomaticallySetting
		]
	);

	const goToPreviousStep = useCallback(
		(): void => {
			if (isNil(previousStep)) {
				return;
			}
			setActiveStep(previousStep);
		},
		[
			previousStep
		]
	);

	const goToNextStep = useCallback(
		async (): Promise<void> => {
			if (isNil(nextStep)) {
				return;
			}

			const stepValidationResult: InputValidationResult = await formHandler
				.validation
				.validateStep(formStepToValidationStep[activeStep]);

			if (stepValidationResult.kind === InputValidationResultKind.Invalid) {
				return;
			}

			setFinishedSteps(x => new Set([...x, activeStep, nextStep]));
			setActiveStep(nextStep);
		},
		[
			activeStep,
			formHandler.validation,
			nextStep
		]
	);

	const activeStepChangeHandler = useCallback(
		(value: FormStep): void => {
			setFinishedSteps(x => new Set([...x, activeStep]));
			setActiveStep(value);
		},
		[activeStep]
	);

	const { title } = formData.eventSaveModel;

	const exportAttendeesHandler = useCallback(
		async (): Promise<void> => {
			if (mode.kind !== "edit") {
				return;
			}

			setIsBusy(true);

			try {
				const attendees: readonly AttendeeExportModel[] = await listAttendeesForExport(
					mode.eventId,
					globalCancellation
				);

				downloadAttendeesAsCsv(title, attendees);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}

			setIsBusy(false);
		},
		[
			alertManager,
			mode,
			globalCancellation,
			title
		]
	);

	const leaveWithConfirmationCheck = useCallback(
		async (leaveAction: () => void): Promise<void> => {
			const isChanged: boolean = isDataChanged
				|| (
					contentStepRef.current
						?.getIsContentChanged() ?? false
				);

			if (isChanged) {
				const confirmation: TryResult<boolean> = await openLeaveComposerDialog({
					shouldShowSaveAsDraft: isSaveAsDraftAvailable
				});

				if (confirmation.wasSuccessful) {
					if (confirmation.result) {
						await saveDraftHandler();
					} else {
						leaveAction();
					}
				}
			} else {
				leaveAction();
			}
		},
		[
			isDataChanged,
			isSaveAsDraftAvailable,
			openLeaveComposerDialog,
			saveDraftHandler
		]
	);

	const backHandler = useMemo(
		(): (() => void) | undefined => {
			return onBack
				? () => leaveWithConfirmationCheck(
					() => onBack(wasAnyCategoryCreated)
				)
				: undefined;
		},
		[
			leaveWithConfirmationCheck,
			onBack,
			wasAnyCategoryCreated
		]
	);

	const closeHandler = useCallback(
		(): void => {
			leaveWithConfirmationCheck(
				() => onClose(wasAnyCategoryCreated)
			);
		},
		[
			leaveWithConfirmationCheck,
			onClose,
			wasAnyCategoryCreated
		]
	);

	const renderFooter = (): ReactElement => (
		<Toolbar>
			{!isMobile && (
				<>
					<Toolbar.Toggle
						value={isInPreviewMode}
						onChange={setIsInPreviewMode}
						text={localize("_Preview")}
						trackingId="preview-mode"
						shouldAutoCloseResponsiveDropOver
					/>
					<Toolbar.SpaceBetween />
				</>
			)}
			<Toolbar.Button
				trackingId="previous"
				kind="secondary"
				text={localize("_Previous")}
				isDisabled={isInPreviewMode || isNil(previousStep)}
				onClick={goToPreviousStep}
			/>
			{state.status === EventStatus.Draft && (
				<Toolbar.Button
					trackingId="save-draft"
					kind="secondary"
					text={localize("_SaveAsDraft")}
					onClick={saveDraftHandler}
					isDisabled={!isSaveAsDraftAvailable}
				/>
			)}
			<Toolbar.Button
				trackingId="next"
				kind="primary"
				text={localize("_Next")}
				isDisabled={isInPreviewMode || isNil(nextStep)}
				onClick={goToNextStep}
			/>
			<Toolbar.Button
				trackingId="publish"
				kind="primary"
				text={
					localize(
						hasPublicationDate && isNotNil(formData.eventSaveModel.publicationDate)
							? "_Submit"
							: "_Publish"
					)
				}
				isDisabled={!isPublishAvailable}
				onClick={publishHandler}
			/>
		</Toolbar>
	);

	const renderPreview = (): ReactElement | null => {
		if (!isInPreviewMode
			|| !state.isLoaded
			|| isMobile
		) {
			return null;
		}

		return (
			<Preview
				model={formData.eventSaveModel}
				createdBy={state.createdBy}
				validation={attachable.validation}
				timezone={userTimezone}
				categoryHierarchy={state.categoryHierarchy}
				hasPublicationDate={hasPublicationDate}
				originalImages={state.originalImages}
			/>
		);
	};

	const attachable: IObjectInputAttachable<EventFormModel> = formHandler
		.for("eventSaveModel");

	return (
		<>
			<HubItemComposerDialog
				title={
					mode.kind === "new" || mode.kind === "duplicate"
						? localize("_NewEvent")
						: localize("_EditEvent")
				}
				onClose={closeHandler}
				onBack={backHandler}
				activeStep={activeStep}
				isBusy={isBusy}
				steps={steps}
				footer={renderFooter()}
				preview={renderPreview()}
				goToNextStep={goToNextStep}
				onActiveStepChanged={activeStepChangeHandler}
				trackingId="event-composer-dialog"
				backdrop={backdropState}
			>
				{status === EventStatus.Archived && state.isLoaded
					? (
						<ArchivedContentStep
							isActive={activeStep === FormStep.Content}
							model={formData.eventSaveModel}
							onChangeIsLoading={setIsBusy}
							originalImages={state.originalImages}
							infoAlerts={infoAlerts}
						/>
					)
					: (
						<ContentStep
							ref={contentStepRef}
							isActive={activeStep === FormStep.Content}
							attachable={attachable}
							imageRestrictions={imageRestrictions}
							defaultImageLocation={defaultImageLocation}
							originalImages={originalImages}
							onChangeIsLoading={setIsBusy}
							shouldFocusTitle={shouldFocusTitle}
							infoAlerts={infoAlerts}
							isPixabayImageGalleryAvailable={isPixabayImageGalleryAvailable}
						/>
					)
				}
				<DetailsStep
					isActive={activeStep === FormStep.Details}
					attachable={attachable}
					status={status}
					infoAlerts={infoAlerts}
					timezone={userTimezone}
					attendeeIdsWithKind={attendeeIdsWithKind}
					isDuplicate={mode.kind === "duplicate"}
				/>
				<SettingsStep
					isActive={activeStep === FormStep.Settings}
					attachable={attachable}
					status={status}
					categoryHierarchy={categoryHierarchy}
					hasPermissionToCreateNewCategory={hasPermissionToCreateNewCategory}
					hasPermissionToSendDirectPopups={canSendDirectPopups}
					onCreateNewCategory={newCategoryHandler}
					infoAlerts={infoAlerts}
					timezone={userTimezone}
					hasPublicationDate={hasPublicationDate}
					onHasPublicationDateChange={setHasPublicationDate}
				/>
				<WhoHasAccessStep
					isActive={activeStep === FormStep.WhoHasAccess}
					attachable={attachable}
					status={status}
					usersAndUserGroupsData={usersAndUserGroupsData}
					infoAlerts={infoAlerts}
				/>
				<AttendeesStep
					isActive={activeStep === FormStep.Attendees}
					allUsers={usersAndUserGroupsData.users}
					attendeeIdsWithKind={attendeeIdsWithKind}
					attachable={attachable}
					status={status}
					infoAlerts={infoAlerts}
					onExport={
						mode.kind === "edit"
							? exportAttendeesHandler
							: undefined
					}
				/>
			</HubItemComposerDialog>
			{editCategoryDialog}
			{confirmationDialogElement}
			{leaveComposerDialogElement}
		</>
	);
};
EditDialog.displayName="EditDialog";

const staticValidationConfiguration: IValidationConfiguration<FormModel> = createValidationConfiguration<FormModel>();
const eventValidation: IObjectValidatorBuilder<EventFormModel> = staticValidationConfiguration
	.forObject("eventSaveModel");

// START - Content step validations
eventValidation
	.forScalar("title")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

eventValidation
	.forCollection("linkedDocuments")
	.add(
		hasMaximumEntries(MAX_LINKED_DOCUMENTS_COUNT)
	);

eventValidation
	.forCollection("linkedDocuments")
	.forItems(getLinkedDocumentId)
	.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);
// END - Content step validations

// START - Details step validations
eventValidation.forScalar("locationType")
	.add(
		isNotNullNorUndefined()
	);

eventValidation.when(
		x => x.locationType === LocationType.Online || x.locationType === LocationType.Hybrid
	)
	.forScalar("onlineEventUrl")
	.add(
		doesNotExceedLength(255),
		isUrl({
			allowHttpsOnly: false
		})
	);

eventValidation.when(
		x => x.locationType === LocationType.Physical || x.locationType === LocationType.Hybrid
	)
	.forScalar("location")
	.add(
		doesNotExceedLength(255)
	);

eventValidation.forScalar("startTime")
	.add(
		isNotNullNorUndefined(),
		isValidDate()
	);
eventValidation.forScalar("signUpDeadline")
	.add(
		isValidDate()
	);
eventValidation.forScalar("availableSpots")
	.add(
		isConvertible(),
		isPositive(),
		isInt32()
	);
eventValidation.forScalar("externalUrl")
	.add(
		isUrl({
			allowHttpsOnly: false
		}),
		doesNotExceedLength(255)
	);
// END - Details step validations

// START - Settings step validations
eventValidation
	.forScalar("categoryId")
	.add(
		isNotNullNorUndefined()
	);
eventValidation
	.forCollection("tags")
	.add(
		hasMaximumEntries(MAX_TAG_COUNT)
	);
eventValidation.forScalar("endTime")
	.add(
		isNotNullNorUndefined(),
		isValidDate()
	);
eventValidation.forScalar("publicationDate")
	.add(
		isValidDate()
	);

eventValidation
	.when(x => !isEndTimeAfterStart(x.startTime, x.endTime))
	.forScalar("endTime")
	.add(
		inline((): InputValidationResult => {
			return {
				kind: InputValidationResultKind.Invalid,
				errorMessage: localize("_EndDateTimeMustBeGreaterThanStartDateTime")
			};
		})
	);

eventValidation
	.when(x => x.isAttendanceEnabled
			&& isEndTimeAfterStart(x.startTime, x.signUpDeadline)
	)
	.forScalar("signUpDeadline")
	.add(
		inline((): InputValidationResult => {
			return {
				kind: InputValidationResultKind.Invalid,
				errorMessage: localize("_SignUpDateTimeMustBeGreaterThanStartDateTime")
			};
		})
	);
// END - Settings step validations

const contentValidationStep: IObjectValidationStepBuilder<EventSaveModel> = configureValidationStep<EventSaveRequestModel>()
	.for("eventSaveModel")
	.add(
		"title",
		"media",
		"layout",
		"linkedDocuments"
	);

const detailsValidationStep: IObjectValidationStepBuilder<EventSaveModel> = configureValidationStep<EventSaveRequestModel>()
	.for("eventSaveModel")
	.add(
		"locationType",
		"startTime",
		"endTime",
		"location",
		"onlineEventUrl",
		"ticketType",
		"signUpDeadline",
		"availableSpots",
		"externalUrl",
		"isAttendanceEnabled"
	);

const settingsValidationStep: IObjectValidationStepBuilder<EventSaveModel> = configureValidationStep<EventSaveRequestModel>()
	.for("eventSaveModel")
	.add(
		"categoryId",
		"tags",
		"showAutomatically",
		"shouldShowAuthor",
		"areCommentsEnabled",
		"publicationDate"
	);

const whoHasAccessValidationStep: IObjectValidationStepBuilder<EventSaveModel> = configureValidationStep<EventSaveRequestModel>()
	.for("eventSaveModel")
	.add(
		"isForEveryoneInTheCategory",
		"access"
	);

const attendeesValidationStep: IObjectValidationStepBuilder<EventSaveModel> = configureValidationStep<EventSaveRequestModel>()
	.for("eventSaveModel")
	.add(
		"removedAttendeeIds"
	);

const formStepToValidationStep: { [key in FormStep]: IObjectValidationStepBuilder<EventSaveModel> } = {
	[FormStep.Content]: contentValidationStep,
	[FormStep.Details]: detailsValidationStep,
	[FormStep.Settings]: settingsValidationStep,
	[FormStep.WhoHasAccess]: whoHasAccessValidationStep,
	[FormStep.Attendees]: attendeesValidationStep,
};

type EditEventState = ConditionalProps<
	{
		categoryHierarchy: readonly HubHierarchyListNodeModel[];
		originalViewModel: EventViewModel;
		originalImages: ExistingHubItemImages;
		hasPermissionToCreateNewCategory: boolean;
		canSendDirectPopups: boolean;
		usersAndUserGroupsData: UsersAndUserGroupsDataModel;
		imageRestrictions: HubItemImageRestrictionsModel;
		defaultImageLocation: DocumentLocationViewModel | null;
		status: EventStatus;
		attendeeIdsWithKind: readonly AttendeeIdWithKind[];
		createdBy: UserIdentityModel | null;
		isPixabayImageGalleryAvailable: boolean;
	},
	"isLoaded"
>;

const initialSteps: readonly Step<FormStep>[] = [
	{
		title: localize("_Content"),
		value: FormStep.Content,
		valueTrackingId: "content-step"
	},
	{
		title: localize("_EventDetails"),
		value: FormStep.Details,
		valueTrackingId: "details-step"
	},
	{
		title: localize("_Settings"),
		value: FormStep.Settings,
		valueTrackingId: "settings-step"
	},
	{
		title: localize("_WhoHasAccess"),
		value: FormStep.WhoHasAccess,
		valueTrackingId: "who-has-access-step"
	}
];

export type EditEventDialogInitialData = Omit<EditDialogProps, "onClose" | "onSave">;

export function useEditEventDialog(): [
	element: ReactElement | null,
	open: (props: EditEventDialogInitialData) => Promise<TryResult<void>>
] {
	return useDataInputDialog(
		(props, onClose, onSave) => (
			<EditDialog
				{...props}
				onClose={onClose}
				onSave={onSave}
			/>
		)
	);
}

export function openEventEditDialog(props: Omit<EditDialogProps, "onClose">): void {
	renderReactDialog(
		onClose => (
			<EditDialog
				{...props}
				onClose={onClose}
			/>
		)
	);
}

async function convertToFormModel(
	viewModel: EventViewModel,
	preferredCategoryId: number | null
): Promise<FormModel> {
	const {
		id,
		title,
		media,
		isForEveryoneInTheCategory,
		access,
		categoryId,
		startTime,
		endTime,
		publicationDate,
		locationType,
		location,
		onlineEventUrl,
		ticketType,
		signUpDeadline,
		availableSpots,
		externalUrl,
		tags,
		showAutomatically,
		linkedDocuments,
		isAttendanceEnabled,
		shouldShowAuthor,
		areCommentsEnabled
	} = viewModel;

	return {
		eventSaveModel: {
			id,
			title,
			layout: isNotNil(viewModel.layout)
				? await convertLayoutToSaveModel(viewModel.layout)
				: { rows: [] },
			media: await convertMediaToSaveModel(media),
			isForEveryoneInTheCategory,
			access,
			categoryId: categoryId ?? preferredCategoryId,
			startTime: parseDtoDate(startTime),
			endTime:  parseDtoDate(endTime),
			publicationDate: parseDtoDate(publicationDate),
			locationType,
			location,
			onlineEventUrl,
			ticketType,
			signUpDeadline: parseDtoDate(signUpDeadline),
			availableSpots,
			externalUrl,
			tags: tags ?? [],
			showAutomatically,
			removedAttendeeIds: [],
			linkedDocuments: linkedDocuments
				?.map(convertToLinkedDocumentFormData) ?? [],
			isAttendanceEnabled,
			shouldShowAuthor,
			areCommentsEnabled
		},
		shouldPublishEvent: false
	};
}

function prepareEvent(
	model: EventFormModel,
	hasPublicationDate: boolean
): EventSaveModel {
	return {
		...model,
		media: cleanUpMedia(model.media),
		linkedDocuments: model.linkedDocuments
			.map(convertToLinkedDocumentSaveModel),
		location: model.locationType === LocationType.Hybrid || model.locationType === LocationType.Physical
			? model.location
			: null,
		onlineEventUrl: model.locationType === LocationType.Hybrid || model.locationType === LocationType.Online
			? model.onlineEventUrl
			: null,
		publicationDate: hasPublicationDate
			? ensureNotNil(model.publicationDate)
			: null,
		availableSpots: model.isAttendanceEnabled
			? model.availableSpots
			: null,
		signUpDeadline: model.isAttendanceEnabled
			? model.signUpDeadline
			: null
	};
}

function beforeUnloadListener(event: BeforeUnloadEvent): string {
	event.preventDefault();
	event.returnValue = localize("_YouHaveUnsavedChanges");
	return event.returnValue;
};

const EMPTY_IMAGE_SET: ExistingHubItemImages = {
	coverImage: null,
	layoutImages: new Map()
};

const EMPTY_USER_AND_USERGROUP_DATA: UsersAndUserGroupsDataModel = {
	users: [],
	userGroups: []
};

export function isEndTimeAfterStart(
	startTime: Date | null,
	endTime: Date | null
): boolean {
	return isNotNil(startTime)
		&& !isNaN(startTime.getTime())
		&& isNotNil(endTime)
		&& !isNaN(endTime.getTime())
		&& endTime > startTime;
};

// Please keep in sync with EventService::GetChangedPropertiesForEventAttendanceNotification
function getChangedPropertiesForEventAttendanceNotification(
	initial: EventSaveModel,
	model: EventSaveModel
): ReadonlySet<PropertyPath<EventSaveModel>> {
	const result = new Set<PropertyPath<EventSaveModel>>();

	if (model.title !== initial.title) {
		result.add("title");
	}

	if (model.locationType !== initial.locationType) {
		result.add("locationType");
	}

	if (model.location !== initial.location) {
		result.add("location");
	}

	if (model.onlineEventUrl !== initial.onlineEventUrl) {
		result.add("onlineEventUrl");
	}

	if (hasDateTimeChanged(model.startTime, initial.startTime)) {
		result.add("startTime");
	}

	if (hasDateTimeChanged(model.endTime, initial.endTime)) {
		result.add("endTime");
	}

	if (model.ticketType !== initial.ticketType) {
		result.add("ticketType");
	}

	if (hasDateTimeChanged(model.signUpDeadline, initial.signUpDeadline)) {
		result.add("signUpDeadline");
	}

	if (model.availableSpots !== initial.availableSpots) {
		result.add("availableSpots");
	}

	if (model.externalUrl !== initial.externalUrl) {
		result.add("externalUrl");
	}

	if (isEventLayoutChanged(model.layout, initial.layout)) {
		result.add("layout");
	}

	if (isLinkedDocumentsChanged(model.linkedDocuments, initial.linkedDocuments)) {
		result.add("linkedDocuments");
	}

	return result;

	function hasDateTimeChanged(
		newDate: Date | null,
		oldDate: Date | null
	): boolean {
		return newDate?.getTime() !== oldDate?.getTime();
	}

	function isEventLayoutChanged(
		newLayout: HubItemLayoutSaveModel,
		oldLayout: HubItemLayoutSaveModel
	): boolean {
		if (newLayout.rows.length !== oldLayout.rows.length) {
			return true;
		}

		for (let r = 0; r < newLayout.rows.length; r++) {
			const newRow: HubItemRowSaveModel = newLayout.rows[r];
			const oldRow: HubItemRowSaveModel = oldLayout.rows[r];

			if (newRow.columns.length !== oldRow.columns.length
				|| newRow.layout !== oldRow.layout
			) {
				return true;
			}

			for (let c = 0; c < newRow.columns.length; c++) {
				const newColumn: HubItemColumnSaveModel = newRow.columns[c];
				const oldColumn: HubItemColumnSaveModel = oldRow.columns[c];

				if (newColumn.elements.length !== oldColumn.elements.length) {
					return true;
				}

				for (let e = 0; e < newColumn.elements.length; e++) {
					const newElement: HubItemElementSaveModel = newColumn.elements[e];
					const oldElement: HubItemElementSaveModel = oldColumn.elements[e];

					if (newElement.kind !== oldElement.kind) {
						return true;
					}

					switch (newElement.kind) {
						case HubItemElementKind.Subtitle:
							if (newElement.text !== (oldElement as ObjectWithClientId<SubtitleElementSaveModel>).text) {
								return true;
							}
							break;
						case HubItemElementKind.Paragraph:
							if (newElement.html !== (oldElement as ObjectWithClientId<ParagraphElementSaveModel>).html) {
								return true;
							}
							break;
						case HubItemElementKind.Image:
							if (newElement.image.isSet) {
								return true;
							}
							break;
						case HubItemElementKind.Embed:
							if (newElement.code !== (oldElement as ObjectWithClientId<EmbedElementSaveModel>).code) {
								return true;
							}
							break;
						case HubItemElementKind.Video:
							if (newElement.url !== (oldElement as ObjectWithClientId<VideoElementSaveModel>).url) {
								return true;
							}
							break;
						case HubItemElementKind.HorizontalLine:
							break;
						case HubItemElementKind.FrequentlyAskedQuestion:
							const {
								items: newItems
							} = newElement;

							const {
								items: oldItems
							} = (oldElement as ObjectWithClientId<FrequentlyAskedQuestionElementSaveModel>);

							if (newItems.length !== oldItems.length) {
								return true;
							}

							for (let i = 0; i < newItems.length; i++) {
								if (newItems[i].question !== oldItems[i].question
									|| newItems[i].answer !== oldItems[i].answer
								) {
									return true;
								}
							};

							break;
						default:
							never(newElement);
					}
				}
			}
		}

		return false;
	}

	function isLinkedDocumentsChanged(
		newLinks: readonly HubLinkedDocumentSaveModel[],
		oldLinks: readonly HubLinkedDocumentSaveModel[]
	): boolean {
		return !deepComparison(newLinks, oldLinks);
	}
}